import React, { ReactElement, useCallback, useEffect, useState } from 'react';
import {
  Card, CardContent, Typography, Grid, Box, IconButton, Menu, Tooltip,
  MenuItem, Popover, ClickAwayListener, Stack, Badge
} from '@mui/material'
import { ClassNameMap, withStyles } from '@mui/styles'
import stylesheet from "../detail-container.stylesheet";
import EditIcon from '@mui/icons-material/Edit'
import AssignmentIcon from '@mui/icons-material/Assignment';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ContentPasteGoIcon from '@mui/icons-material/ContentPasteGo';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DoubleArrowRoundedIcon from '@mui/icons-material/DoubleArrowRounded';
import EditNoteIcon from '@mui/icons-material/EditNote';
import PrivacyTipOutlinedIcon from '@mui/icons-material/PrivacyTipOutlined';
import NoteAddOutlinedIcon from '@mui/icons-material/NoteAddOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import MoreVertOutlinedIcon from '@mui/icons-material/MoreVertOutlined';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import HelpOutlineOutlinedIcon from '@mui/icons-material/HelpOutlineOutlined';
import ColorBubbleComponent from './color-bubble.component'
import { checkCondition } from './condition-check';
import ContextualHelpComponent from "./contextual-help.component";
import MedEvidenceComponent from './med-evidence.component';
import RationaleDialogComponent from './rationale-dialog.component';
import { RootState } from '../../../../redux/store';
import { useSelector } from 'react-redux';
import Helper from '../../../../utils/helper';
import { InfoSharp } from '@mui/icons-material';
import moment from 'moment';
import DependentInfoComponent from './dependent-info.component';
import DiagnosticImpactIndicator from './diagnostic-impact-indicator.component';

interface IIcons {
  iconType: string,
  callback: any
}

interface IProps {
  id: number,
  name?: any
  input?: any,
  leftHeader: any | ReactElement,
  leftHeader1?: string | ReactElement,
  rightHeader: string | ReactElement,
  subHeader?: string | ReactElement,
  subHeader1?: string | ReactElement,
  bottomText: string | ReactElement,
  source?: any,
  icons: IIcons[],
  needs_confirmation: boolean | undefined,
  needs_update: boolean | undefined,
  is_recommended?: boolean,
  programs?: string[],
  overridenPrograms?: string[],
  classes: ClassNameMap,
  is_referred: boolean,
  is_overridden: boolean,
  isOverrideItem?: boolean,
  last_sync_date?: string,
  onMedDetailsClick?: any,
  onRefClick: any,
  onDeleteMedicationClick?: any,
  onOverrideClick?: any,
  patientId: string,
  entityRef: string,
  is_care_plan_non_recc?: boolean
  category?: string
  titration_status?: any
  recommendation_source?: '',
  is_new_med?: any
  showDiagnosticIndicator?: boolean
  modified_by_name?: string,
  modified_on?: number
}
interface IRefState {
  created_on: number;
  detail: string
}

const HtmlTooltip = withStyles((theme) => ({
  tooltip: {
    backgroundColor: 'var(--color-paper)',
    color: 'var(--color-text-primary)',
    maxWidth: 220,
    fontSize: '0.7rem',
    border: '0.1rem solid #DB2323',
    //height: '20px!important'
  },
  arrow: {
    color: '#DB2323'
  }
}))(Tooltip);

const CardLayoutComponent: React.FC<IProps> = ({ id, name, input, leftHeader, leftHeader1, rightHeader, subHeader,
  subHeader1, bottomText, source, icons, needs_confirmation, needs_update, is_recommended = false,
  programs = [], overridenPrograms = [], classes, is_referred, is_overridden, isOverrideItem, last_sync_date, onMedDetailsClick,
  onDeleteMedicationClick, onRefClick, onOverrideClick, patientId, entityRef, is_care_plan_non_recc,
  category = '', titration_status = '', recommendation_source = '', is_new_med = false, showDiagnosticIndicator = false,
  modified_by_name, modified_on }) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false)
  const [openDialog, setOpenDialog] = useState(false)
  const [infoDialog, setInfoDialog] = useState(false)
  const [openMedEvidenceDialog, setOpenMedEvidenceDialog] = useState(false)
  const [cardAnchorEl, setCardAnchorEl] = useState<null | HTMLElement>(null);
  const [openRationale, setOpenRationale] = useState(false)
  const patientDetail = useSelector((state: RootState) => state.patientDetail.patientDetail)

  const getProgramName = (prg: string) => patientDetail?.program_enrollments
    ?.filter((m: { program: any }) => m.program === prg)?.[0]?.program_display_name || prg

  let prgStatus = Helper.isPrgInActive(patientDetail?.program_enrollments, programs)
  let prgStatusColor = {
    fill: prgStatus ? 'rgba(0, 0, 0, 0.26) !important' : '',
    cursor: prgStatus ? 'default' : 'hand'
  }
  useEffect(() => {
    prgStatus = Helper.isPrgInActive(patientDetail?.program_enrollments, programs)
    prgStatusColor = {
      fill: prgStatus ? 'rgba(0, 0, 0, 0.26) !important' : '',
      cursor: prgStatus ? 'default' : 'hand'
    }
  }, [patientDetail])

  const sortProgramNyName = () => {
    if (programs?.length <= 1)
      return programs || []

    return programs?.map((a: any) => getProgramName(a))
      ?.sort((a: any, b: any) => a.toLowerCase() > b.toLowerCase() ? 1 : -1)
      ?.map((a: any) =>
        patientDetail?.program_enrollments?.filter((m: { program_display_name: any }) => m.program_display_name === a)?.[0]
          ?.program || a) || programs
  }

  // Generate tooltip content for "last saved by" info
  const getModifiedByTooltip = () => {
    if (modified_by_name) {
      const modDate = modified_on ? moment.unix(modified_on).format("MMM DD, YYYY h:mm a") : ''
      return `Last saved by ${modified_by_name}${modDate ? ` on ${modDate}` : ''}`
    }
    return ''
  }

  const handleCardClick = (event: React.MouseEvent<HTMLElement>) => {
    setCardAnchorEl(event.currentTarget);
  };

  const handleCardClose = () => {
    setCardAnchorEl(null);
  };

  const onNoRef = (event: any) => {
    setOpen(false)
    setAnchorEl(event.currentTarget);
  }

  const handleMoreClick = (event: any) => {
    setOpen(false)
    onNoRef(event)
  };

  const handleClose = () => {
    setOpen(false)
    setAnchorEl(null);
  };

  const getIcons = (_value: string) => {
    let icon
    switch (_value) {
      case 'edit':
        icon = <EditIcon sx={prgStatusColor} className={classes.editIcon} />
        break
      case 'description':
        icon = <InsertDriveFileOutlinedIcon className={classes.iconFontSize} />
        break
      case 'visible':
        icon = <VisibilityOutlinedIcon className={classes.iconFontSize} />
        break
      case 'more':
        icon = <MoreVertOutlinedIcon className={classes.iconFontSize} />
        break
      case 'delete':
        icon = <DeleteOutlinedIcon sx={prgStatusColor} className={classes.iconFontSize} />
        break
      default:
        icon = <span></span>
        break
    }
    return icon
  }

  const getCompactIcons = (_value: string) => {
    let icon
    switch (_value) {
      case 'edit':
        icon = <EditIcon sx={prgStatusColor} className={classes.editIcon} />
        break
      case 'mark_deferred':
        icon = <HourglassBottomIcon sx={prgStatusColor} className={classes.editDefIcon} />
        break
      case 'mark_ordered':
        icon = <ReceiptLongIcon sx={prgStatusColor} className={classes.editIcon} />
        break
      case 'mark_result':
        icon = <ContentPasteGoIcon sx={prgStatusColor} className={classes.editIcon} />
        break
      case 'description':
        icon = <InsertDriveFileOutlinedIcon sx={prgStatusColor} className={classes.noteIcon} />
        break
      case 'visible':
        icon = <VisibilityOutlinedIcon className={classes.visibleIconSize} style={{ paddingTop: '0.25rem' }} />
        break
      case 'delete':
        icon = <DeleteOutlinedIcon sx={prgStatusColor} className={classes.compactMenuIcon} style={{ paddingBottom: '0.2rem' }} />
        break
      case 'sync':
        icon = <RefreshIcon sx={prgStatusColor} style={{ fontSize: '0.9rem', paddingTop: '0.4rem' }} />
        break
      case 'synced':
        icon = <CheckCircleIcon style={{ fontSize: '0.9rem', paddingTop: '0.4rem', color: '#4caf50' }} />
        break
      default:
        icon = <span></span>
        break
    }
    return icon
  }

  const getActions = useCallback(
    (_id: any, _value: string, _confirmOrUpdate: boolean | undefined, _callBackFunction: any, _is_recommended: boolean, isPatientUpdate: boolean, _classes: any) => {
      return <IconButton
        disabled={(prgStatus || isPatientUpdate)}
        onClick={() => checkCondition(typeof (_callBackFunction) === 'function', _callBackFunction(_id))}
        className={
          (_confirmOrUpdate && _value === 'edit')
            ? isPatientUpdate ? _classes.corfirmDisabledButtonStyle : _classes.corfirmButtonStyle
            : _classes.iconBaseBorder}
      >
        <Typography style={checkCondition((_is_recommended && _value !== 'edit'), { color: '#FFFFFF', height: '1.25rem' }, { height: '1.25rem' })} >{getIcons(_value)} </Typography>
      </IconButton>
    },
    [anchorEl],
  )

  const getMoreActions = (
    _id: any,
    _value: string,
    _confirmOrUpdate: boolean | undefined, _callBackFunction: any,
    _is_recommended: boolean,
    prgStatus: boolean,
    _classes: any) => {

    return icons.map((e, i, arr) => {
      if (e.iconType !== 'more') {
        return <Tooltip
          title={e.iconType.includes('mark')
            ? e.iconType.charAt(0).toUpperCase() + e.iconType.slice(1).replaceAll('_', ' ')
            : ''
          } placement="bottom" arrow key={`tooltip-${i}`}
        >
          <MenuItem key={`more-actions-${i}`} className={classes.compactMenuItem}>
            <IconButton
              onClick={() => {
                if (e.iconType === 'visible' || !prgStatus) {
                  handleCardClose()
                  checkCondition(typeof (e.callback) === 'function',
                    e.iconType.includes('mark')
                      ? e.callback(_id, e.iconType)
                      : e.callback(_id))
                }
              }}
              className={checkCondition(((_confirmOrUpdate || entityRef === 'med_reconcilation') && e.iconType === ('edit') && !prgStatus), _classes.corfirmCompactButtonStyle, _classes.zeroPadding)}
            >
              <Typography align="center" sx={checkCondition((_is_recommended && e.iconType !== 'edit'), { color: '#FFFFFF' }, {})} >
                {getCompactIcons(e.iconType)}
              </Typography>
            </IconButton>
          </MenuItem>
        </Tooltip>
      }
    })
  }

  const isGreyCard = () => ((titration_status).includes('downtitrate')
    || (titration_status).includes('discontinue')
    || (titration_status).includes('prior dose')
    || (titration_status).includes('lowest dose')
  )

  const getClass = () => {
    let style
    switch (true) {
      case input?.isdDependentQue === true:
        style = classes.dependentQuestionnarie
        break;
      case isOverrideItem === true:
        style = classes.overrideItemCard
        break;
      case entityRef === 'med_reconcilation' && is_new_med && needs_confirmation:
        style = classes.reconcileBgNewMedNeedConfirmation
        break;
      case entityRef === 'med_reconcilation' && is_new_med:
        style = classes.reconcileBgNewMed
        break;
      case entityRef === 'med_reconcilation_mapped':
        style = classes.reconcileBg
        break;
      case entityRef === 'med_reconcilation_quarantine':
        style = classes.reconcileQuarantineBg
        break
      case is_referred && recommendation_source.includes('provider') && is_recommended:
        style = (
          titration_status).includes('initiate')
          ? classes.recommendedRefDisconBackground
          : isGreyCard()
            ? classes.recommendedRefDisconBackground
            : classes.refRecomendBackground
        break
      case is_referred:
        style = classes.referralBackground
        break
      case is_overridden:
        style = classes.overrideBackground
        break
      case is_recommended:
        style = (recommendation_source.includes('provider') && isGreyCard())
          ? classes.recommendedDisconBackground
          : classes.recommendedBackground
        break
      case needs_update:
        style = classes.confirmCardBackground
        break
      case needs_confirmation:
        style = classes.confirmCardBackground
        break
      case is_care_plan_non_recc:
        style = classes.carePlanBorder
        break
      default:
        style = '-'
        break
    }
    return style
  }

  const onHelpClick = () => {
    handleCardClose()
    setOpenDialog(true)
  }

  const onInfoClick = () => {
    handleCardClose()
    setInfoDialog(true)
  }

  const closeInfoDialog = () => {
    setInfoDialog(false)
  }

  const handleRationale = () => {
    handleCardClose()
    setOpenRationale(true)
  }

  const handleRationaleClose = () => setOpenRationale(false)

  const onMedEvidenceClick = () => {
    handleMedEvidenceCardClose()
    setOpenMedEvidenceDialog(true)
  }

  const handleMedEvidenceCardClose = () => setOpenMedEvidenceDialog(false)

  const closeDialog = () => setOpenDialog(false)

  const closeMedEvidenceDialog = () => setOpenMedEvidenceDialog(false)

  const getCardMoreActions = (isMedCard: Boolean) => {
    return <Typography align="right">
      {icons.map((e, i, arr) =>
        <React.Fragment key={`${id}-${i}`}>
          {checkCondition(e.iconType === 'more',
            <React.Fragment key={`${id}-${e.iconType}`}>
              <Popover
                id={`compact-menu-${id}`}
                open={Boolean(cardAnchorEl)}
                anchorEl={cardAnchorEl}
                onClose={handleCardClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right', }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
              >
                <Stack className={classes.compactMenuCard} direction={'row'} justifyContent={'center'} alignItems={'center'}>
                  {last_sync_date && entityRef == 'measurement' &&
                    <MenuItem className={classes.compactMenuItem} >
                      <Typography color="textSecondary" variant="subtitle2">
                        <small>{last_sync_date}</small>
                      </Typography>
                    </MenuItem>
                  }
                  {getMoreActions(id, e.iconType, needs_confirmation ?? needs_update, e.callback, is_recommended, prgStatus, classes)}
                  {!['diagnostic_test', 'threshold', 'clinical_scores', 'icd10_codes', 'monitor',
                    'other_med_discontinue', 'med_reconcilation', 'med_reconcilation_mapped',
                    'med_reconcilation_quarantine', 'toxicities', 'problem_conditions'
                  ].includes(entityRef) &&
                    <Tooltip title="Referral">
                      <MenuItem className={classes.compactMenuItem} onClick={() => !prgStatus && onRefClick()}>
                        <NotificationsActiveOutlinedIcon sx={prgStatusColor} className={classes.notValCompactMenuIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {((['threshold', 'measurement', 'calculator', 'clinical_scores'].includes(entityRef) && (needs_update || needs_confirmation)
                    || entityRef === 'care_plan'
                  ))
                    && <MenuItem className={classes.compactMenuItem} onClick={() => !prgStatus && onOverrideClick()}>
                      <DoubleArrowRoundedIcon sx={prgStatusColor} className={classes.overideIcon} />
                    </MenuItem>
                  }
                  {(['condition_evaluation'].includes(entityRef) ||
                    ('measurement' === entityRef &&
                      !Helper.isEvaluationPrg(patientDetail?.program_enrollments || [])
                    )
                  ) &&
                    <Tooltip title="Rationale">
                      <MenuItem className={classes.compactMenuItem} onClick={() => !prgStatus && handleRationale()}>
                        <PrivacyTipOutlinedIcon sx={prgStatusColor} className={classes.rationaleIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {['care_plan', 'other_medication', 'other_med_discontinue', 'med_reconcilation', 'med_reconcilation_mapped', 'med_reconcilation_quarantine'].includes(entityRef) &&
                    <Tooltip title="Med details">
                      <MenuItem className={classes.compactMenuItem}
                        onClick={() => {
                          handleCardClose();
                          onMedDetailsClick();
                        }}
                      >
                        <InfoOutlinedIcon className={classes.overideIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {['reported_med_intolerance'].includes(entityRef) &&
                    <Tooltip title="Restricted Med details">
                      <MenuItem className={classes.compactMenuItem}
                        onClick={() => {
                          handleCardClose();
                          onMedDetailsClick()
                        }}
                      >
                        <InfoOutlinedIcon className={classes.overideIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {entityRef === 'care_plan' &&
                    <Tooltip title="Med evidence">
                      <MenuItem className={classes.compactMenuItem}
                        onClick={() => {
                          handleCardClose();
                          onMedEvidenceClick()
                        }}
                      >
                        <NoteAddOutlinedIcon fontSize="small" className={classes.overideIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {(['problem_conditions', 'diagnostic_test'].includes(entityRef)) &&
                    <Tooltip title={input?.synopsis || 'Synopsis'}>
                      <MenuItem className={classes.compactMenuItem}>
                        <EditNoteIcon sx={{ fontSize: '1rem' }} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {(['threshold', 'problem_conditions', 'diagnostic_test'].includes(entityRef)
                    || (category === 'conditions' && entityRef === 'calculator')) &&
                    <Tooltip title="Dependent info">
                      <MenuItem className={classes.compactMenuItem} onClick={() => onInfoClick()}>
                        <AssignmentIcon className={classes.noteIcon} sx={{ color: '#757575' }} />
                      </MenuItem>
                    </Tooltip>
                  }
                  {/* Diagnostic Impact indicator for threshold cards */}
                  {entityRef === 'threshold' && (input?.conditions?.length > 0) && (
                    <MenuItem className={classes.compactMenuItem} sx={{ p: 0 }}>
                      <DiagnosticImpactIndicator condPerAssessments={input.conditions} />
                    </MenuItem>
                  )}
                  {(!['diagnostic_test', 'toxicities', 'problem', 'monitor', 'program_lifestyle', 'problem_conditions', 'threshold'].includes(entityRef) &&
                    !(entityRef === 'calculator' && category === 'conditions'))
                    &&
                    <Tooltip title="Help">
                      <MenuItem className={classes.compactMenuItem} onClick={() => onHelpClick()}>
                        <HelpOutlineOutlinedIcon className={classes.contextHelpIcon} />
                      </MenuItem>
                    </Tooltip>
                  }
                </Stack>
              </Popover>
            </React.Fragment>
          )}
        </React.Fragment>
      )}
    </Typography>
  }

  const renderMiniCard = () => {
    return <Box mb={1} className={classes.customMiniCard}>
      <Card variant="outlined" className={classes.compactCard}>
        <CardContent
          className={[getClass(), classes.cardsContentPadding].join(' ')}
          sx={{
            border: entityRef === 'threshold' ? 'none !important' : 'inherit',
            marginLeft: entityRef === "problem_conditions"
              ? '-0.32rem !important'
              : entityRef === "threshold"
                ? '-0.2rem !important'
                : 'inherit'
          }}
        >
          <Grid container onClick={handleCardClick} justifyContent={'flex-start'} alignItems={'center'}
            className={classes.compactCardContent}
            sx={{ flexWrap: entityRef === 'problem_conditions' ? 'nowrap !important' : 'wrap' }}
          >
            <Grid item md sx={{ width: '88%' }}>
              <Stack spacing={0.35} direction="row" alignItems={'center'} flexWrap={'wrap'}>
                <Stack direction="row" alignItems="center" spacing={0.2} justifyContent={'flex-start'} >
                  <Typography variant="body2" className={classes.desc}
                    sx={is_recommended
                      ? { color: 'var(--color-highlight)' }
                      : entityRef === 'threshold' && is_overridden
                        ? { color: '#ffb573' }
                        : {}
                    }>
                    {leftHeader}
                    <ColorBubbleComponent selectedProgramList={programs} overridenProgramList={overridenPrograms} />
                  </Typography>
                  {/* Show inline indicator only when explicitly enabled (dialog/batch edit views) */}
                  {showDiagnosticIndicator && (input?.cond_per_assessments?.length > 0 || input?.conditions?.length > 0) && (
                    <DiagnosticImpactIndicator condPerAssessments={input.cond_per_assessments || input.conditions} />
                  )}
                </Stack>
                {subHeader &&
                  <Typography color="textSecondary" variant="body2" className={classes.desc}>
                    <small>{subHeader}</small>
                  </Typography>
                }
              </Stack>
            </Grid>
            <Grid item
              sx={{
                minWidth: '8%',
                width: entityRef === "problem_conditions" ? '22%' : 'fit-content !important'
              }}
            >
              {entityRef === "problem_conditions"
                ? <Tooltip title={getModifiedByTooltip()} arrow placement="top">
                    <Typography className={classes.subHeaderTitle} align="right" variant="subtitle2">
                      {bottomText}
                    </Typography>
                  </Tooltip>
                : <Stack direction="row" alignItems={'center'} justifyContent="end" spacing={1} pr={0.5}>
                  <Tooltip title={getModifiedByTooltip()} arrow placement="top">
                    <Typography className={classes.subHeaderTitle} align="right" variant="subtitle2" color="textSecondary">
                      <small>{bottomText}</small>
                    </Typography>
                  </Tooltip>
                  {(!['conditions', 'diagnosis'].includes(category) && entityRef !== 'calculator') &&
                    <Typography sx={{ minWidth: '1.2rem', lineHeight: '1 !important' }}
                      textAlign={entityRef === 'threshold' ? 'center' : 'left'}
                      className={classes.subTitle}
                    >
                      {rightHeader}
                    </Typography>
                  }
                </Stack>
              }
            </Grid>
          </Grid>
        </CardContent>
      </Card>
      {!['icd10_codes'].includes(entityRef) && getCardMoreActions(false)}
    </Box >
  }

  const getMedIntolId = () => {
    return entityRef === 'reported_med_intolerance'
      ? `${leftHeader?.med_id}/${leftHeader?.med_type}`
      : id
  }

  const renderMedCard = () => {
    return <Box className={classes.fullWidth}>
      <Card variant="outlined" className={entityRef === 'care_plan' ? classes.compactMedCard : classes.customCard}>
        <CardContent className={[getClass(), classes.zeroPadding].join(' ')} sx={{ padding: '0rem 0.1rem !important' }}>
          <Grid container spacing={0.15} onClick={handleCardClick} direction='column' className={classes.compactCardContent}>
            <Grid item className={classes.fullWidth}>
              <Grid container spacing={0} direction="row" justifyContent="flex-start">
                <Grid item className={rightHeader !== '' ? classes.evalTitleHeader : classes.fullWidth}>
                  <Typography variant="body2" className={classes.desc} sx={checkCondition(is_recommended, { color: 'var(--color-highlight)' }, {})}>
                    {entityRef === 'reported_med_intolerance' ? leftHeader?.name : leftHeader}
                    <ColorBubbleComponent selectedProgramList={programs} />
                  </Typography>
                </Grid>
                <Grid item xs>
                  <Typography variant="body2" className={classes.desc} sx={checkCondition(is_recommended, { color: '#FFFFFF' }, {})} align="right">
                    {rightHeader}
                  </Typography>
                </Grid>
              </Grid>
            </Grid>
            {source &&
              <Grid item className={classes.subHeaderTitle}>
                <Typography className={classes.desc} variant="subtitle2" color="textSecondary">
                  {source}
                </Typography>
              </Grid>
            }
            <Grid item className={classes.fullWidth}>
              <Grid container spacing={0} direction="row" alignContent={'center'} alignItems={'baseline'}>
                <Grid item xs className={classes.subHeaderTitle}>
                  <Typography
                    color={checkCondition(is_recommended, undefined, 'textSecondary')}
                    className={classes.desc}
                    variant="subtitle2"
                    onClick={handleCardClick}
                  >
                    {subHeader}
                  </Typography>
                </Grid>
                <Grid item sx={{ minWidth: '5%', width: 'fit-content !important' }}>
                  <Tooltip title={getModifiedByTooltip()} arrow placement="top">
                    <Typography className={classes.cardDate} textAlign={'right'} variant="subtitle2" color="textSecondary">
                      <small>{bottomText}</small>
                    </Typography>
                  </Tooltip>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
          {getCardMoreActions(true)}
        </CardContent>
      </Card>
    </Box>
  }

  // Get background color for recommended cards - mirrors getClass() logic for sx override
  const getRecommendedBgColor = () => {
    if (is_referred && recommendation_source.includes('provider') && is_recommended) {
      if (titration_status?.includes('initiate') || isGreyCard()) {
        return '#373534a8'; // recommendedRefDisconBackground
      }
      return '#52514F'; // refRecomendBackground
    }
    if (is_recommended) {
      if (recommendation_source.includes('provider') && isGreyCard()) {
        return '#373534a8'; // recommendedDisconBackground
      }
      return 'var(--color-recommended-bg)'; // recommendedBackground
    }
    return undefined;
  };

  const renderRecommendedMed = () => {
    // Use sx prop for background to override global MuiPaper-root !important styles
    const cardBgColor = getRecommendedBgColor();
    return <Card variant="outlined" className={`${getClass()} ${classes.fullWidth}`} sx={{ backgroundColor: cardBgColor ? `${cardBgColor} !important` : undefined }}>
      <CardContent className={classes.programCardContent} sx={{ padding: entityRef === 'symptom_evaluation' ? '3px 5px 3px 8px !important' : 'inherit' }}>
        <Grid container direction="row" justifyContent="space-between" alignItems="center" className={classes.initialFlexwrap}>
          <Grid item width={rightHeader ? '72%' : '100%'}>
            <Typography variant="subtitle2" className={classes.desc} sx={{ color: is_recommended ? 'var(--color-highlight)' : '' }}>
              {leftHeader}
              <ColorBubbleComponent selectedProgramList={programs} />
            </Typography>
          </Grid>
          <Grid item className={classes.subTitle} width={'auto'}>
            <Typography variant="body2" className={classes.desc} align="right" sx={{ color: is_referred ? '#1e141487' : '#FFF' }}>
              {rightHeader}
            </Typography>
          </Grid>
        </Grid>
        {checkCondition(subHeader, <Grid container direction="row" alignItems="baseline" className={classes.initialFlexwrap} >
          <Grid item className={classes.subHeaderTitle}>
            <Typography variant="subtitle2" sx={{ color: (entityRef === 'symptom_evaluation' || is_referred) ? '#1e141487' : '#FFF' }}>
              {subHeader}
            </Typography>
          </Grid>
        </Grid>)}
        {checkCondition(leftHeader1,
          <Grid container direction="row" justifyContent="space-between" alignItems="baseline" className={classes.initialFlexwrap}>
            <Grid item>
              <Typography className={classes.descleftHeader} variant="subtitle2">
                {leftHeader1}
              </Typography>
            </Grid>
          </Grid>
        )}
        {checkCondition(subHeader1, <Grid container direction="row" alignItems="baseline" className={classes.initialFlexwrap} >
          <Grid item className={classes.subHeaderTitle}>
            <Typography sx={{ color: is_referred ? '#1e141487' : '#FFF' }} variant="subtitle2">
              {subHeader1}
            </Typography>
          </Grid>
        </Grid>
        )}
        {source &&
          <Grid item className={classes.subHeaderTitle}>
            <Typography className={classes.desc} sx={{ color: is_referred ? '#1e141487' : '#FFF' }} variant="subtitle2">
              {source}
            </Typography>
          </Grid>
        }
        <Grid container direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap={"nowrap"}>
          <Grid item xs={11} className={classes.subHeaderTitle}>
            {input?.recommendation_date &&
              <Typography sx={{ color: is_referred ? '#1e141487' : '#FFF' }} variant="subtitle2" pt={0.3}>
                {`Created on ${moment.unix(input.recommendation_date).format('DD MMM, YYYY hh:mm A')}`}
              </Typography>
            }
            <Tooltip title={getModifiedByTooltip()} arrow placement="top">
              <Typography sx={{ color: is_referred ? '#1e141487' : '#FFF' }} variant="subtitle2">
                {bottomText}
              </Typography>
            </Tooltip>
          </Grid>
          <Grid item xs={2}>
            <Typography align="right">
              {icons.map((e, i, arr) =>
                <React.Fragment key={`cl-${e.iconType}-${i}`}>
                  {checkCondition(e.iconType !== 'more',
                    <>{getActions(id, e.iconType, needs_confirmation ?? needs_update, e.callback, is_recommended, input?.isDisabled || false, classes)}</>
                  )}
                  {checkCondition(e.iconType === 'more',
                    <>
                      <ClickAwayListener onClickAway={() => handleClose()}>
                        <HtmlTooltip
                          PopperProps={{
                            disablePortal: true,
                          }}
                          onClose={() => handleClose()}
                          open={open}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          placement="bottom-end"
                          title={<></>}
                          // interactive={true}
                          arrow>
                          <IconButton aria-controls="simple-menu" aria-haspopup="true" size="small"
                            style={{ paddingLeft: '0px' }}
                            disabled={input?.isDisabled}
                            onClick={(event) => handleMoreClick(event)}
                          >
                            <MoreVertOutlinedIcon style={checkCondition(is_recommended, { color: input?.isDisabled ? 'grey' : '#FFFFFF', fontSize: "1rem" }, { fontSize: "1rem" })} />
                          </IconButton>
                        </HtmlTooltip>
                      </ClickAwayListener>
                      <Menu
                        id="simple-menu"
                        MenuListProps={{ disablePadding: true }}
                        anchorEl={anchorEl}
                        keepMounted
                        open={Boolean(anchorEl)}
                        onClose={() => handleClose()}
                        anchorOrigin={{ vertical: "top", horizontal: "left", }}
                        transformOrigin={{ vertical: "top", horizontal: "left", }}
                        sx={{
                          '.MuiMenuItem-root': {
                            padding: '0.3rem 0.5rem !important',
                          },
                        }}
                      >
                        <MenuItem sx={prgStatusColor} onClick={() => !prgStatus && onDeleteMedicationClick()}>
                          <DeleteOutlinedIcon sx={prgStatusColor} fontSize="small" className={classes.notActiveIcon} />
                          <Typography sx={prgStatusColor} variant="body2" className={classes.desc}>Delete</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => onHelpClick()}>
                          <HelpOutlineOutlinedIcon fontSize="small" className={classes.notActiveIcon} />
                          <Typography variant="body2" className={classes.desc}>Help</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => onMedDetailsClick()}>
                          <InfoOutlinedIcon fontSize="small" className={classes.notActiveIcon} />
                          <Typography variant="body2" className={classes.desc}>Med details</Typography>
                        </MenuItem>
                        <MenuItem onClick={() => onMedEvidenceClick()}>
                          <NoteAddOutlinedIcon fontSize="small" className={classes.notActiveIcon} />
                          <Typography variant="body2" className={classes.desc}>Med evidence</Typography>
                        </MenuItem>
                        <MenuItem sx={prgStatusColor} onClick={() => !prgStatus && onRefClick()}>
                          <NotificationsActiveOutlinedIcon sx={prgStatusColor} fontSize="small" className={classes.notActiveIcon} />
                          <Typography sx={prgStatusColor} variant="body2" className={classes.desc}>Referral</Typography>
                        </MenuItem>
                      </Menu>
                    </>
                  )}
                </React.Fragment>
              )}
            </Typography>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  }

  return (<>
    <Grid container direction="column">
      {['measurements', 'conditions', 'diagnosis'].includes(category)
        ? renderMiniCard()
        : <>
          {(category === 'medications' &&
            ['clinical_scores', 'med_reconcilation', 'med_reconcilation_quarantine', 'med_reconcilation_mapped', 'other_medication', 'other_med_discontinue', 'monitor', 'program_lifestyle', 'medications', 'reported_med_intolerance', 'care_plan', 'problems', 'toxicities'].includes(entityRef))
            ? renderMedCard()
            : <Box className={`${classes.customCard} ${classes.fullWidth}`}>
              {(!(recommendation_source.includes('provider') && isGreyCard()) &&
                (input?.isDependentOverridden ||
                  (input?.pendingCount !== input?.totalRecomNeedsUpdate) ||
                  input?.recommendation_date === null)
              ) ? <Badge className={classes.fullWidth}
                badgeContent={
                  <Box
                    sx={{
                      padding: `0rem ${(input?.isDependentOverridden &&
                        (input?.pendingCount !== input?.totalRecomNeedsUpdate || input?.recommendation_date === null))
                        ? '1.8rem'
                        : '0.6rem'} 0.35rem 0rem !important`
                    }}>
                    <Stack direction={'row'} justifyContent="space-between" alignItems="baseline">
                      {input?.recommendation_date === null || (!input.approval_status &&
                        ['med_baseline', 'med_initiation'].includes(input?.stage) &&
                        input?.pendingCount !== input?.totalRecomNeedsUpdate
                      ) &&
                        <Tooltip
                          title={input?.recommendation_date === null
                            ? `Recommendation date is null`
                            : `Recommendation is old`}>
                          <InfoSharp className={classes.staleBadge} sx={{ fill: '#f1c40fc7' }} />
                        </Tooltip>
                      }
                      {input?.isDependentOverridden &&
                        <Tooltip title="Dependents are overridden.">
                          <InfoSharp className={classes.staleBadge} sx={{ fill: 'var(--color-primary)' }} />
                        </Tooltip>}
                    </Stack>
                  </Box>}
              >
                {renderRecommendedMed()}
              </Badge>
                : renderRecommendedMed()
              }
            </Box>
          }
        </>
      }
    </Grid>

    {openDialog &&
      <ContextualHelpComponent
        open={openDialog}
        handleClose={closeDialog}
        id={getMedIntolId()}
        title={leftHeader?.name || leftHeader}
        programs={programs}
        category={category}
      />
    }
    {infoDialog &&
      <DependentInfoComponent
        open={infoDialog}
        input={{
          title: leftHeader?.name || leftHeader,
          isDiagnosticTest: input?.isDiagnosticTest,
          programs: programs || [],
          dependent_questionnaires: input?.dependent_questionnaires || [],
          dependent_measurements: input?.dependent_measurements || [],
          tests_per_cond: input?.tests_per_cond || [],
          conditions: input?.conditions || [],
          threshold: input?.threshold || [],
          synopsis: input?.synopsis || '',
          rationale: input?.rationale || '',
          category: category
        }}
        handleClose={closeInfoDialog}
      />
    }
    {openRationale && (
      <RationaleDialogComponent
        open={openRationale}
        title={leftHeader?.name || leftHeader}
        category={entityRef.replaceAll('_', ' ')}
        input={{
          program: sortProgramNyName(),
          name: name || ''
        }}
        handleClose={handleRationaleClose}
      />
    )}
    {openMedEvidenceDialog &&
      <MedEvidenceComponent
        open={openMedEvidenceDialog}
        handleClose={closeMedEvidenceDialog}
        title={leftHeader?.name || leftHeader}
        input={{
          patientId: patientId,
          program: programs,
          med_id: id
        }}

      />
    }
  </>)
}

export default withStyles(stylesheet)(CardLayoutComponent);